import { useState, useEffect } from 'react'

interface PriceData {
  currentPrice: string
  comparePrice: string
  isLoading: boolean
}

const PRICE_MAP: Record<string, { current: string; compare: string }> = {
  US: { current: '$11.00', compare: '$22.00' },
  EC: { current: '$11.00', compare: '$24.00' },
  MX: { current: '$235.00 MXN', compare: '$500.00 MXN' },
  CA: { current: '$18.10 CAD', compare: '$40.00 CAD' },
  CL: { current: '$12.300 CLP', compare: '$25.000 CLP' },
  CO: { current: '$42.000 COP', compare: '$84.000 COP' },
  PE: { current: 'S/ 40.00', compare: 'S/ 90.00' },
  UY: { current: '$462.00 UYU', compare: '$900.00 UYU' },
  PY: { current: 'Gs. 73.000', compare: 'Gs. 150.000' },
  BO: { current: 'Bs 82.00', compare: 'Bs 200.00' },
}

const DEFAULT_PRICES = PRICE_MAP.US

let cachedCountry: string | null = null

// Timezone → country mapping for Latin America
const TIMEZONE_TO_COUNTRY: Record<string, string> = {
  'America/Mexico_City': 'MX',
  'America/Cancun': 'MX',
  'America/Merida': 'MX',
  'America/Monterrey': 'MX',
  'America/Matamoros': 'MX',
  'America/Mazatlan': 'MX',
  'America/Chihuahua': 'MX',
  'America/Ojinaga': 'MX',
  'America/Hermosillo': 'MX',
  'America/Tijuana': 'MX',
  'America/Bahia_Banderas': 'MX',
  'America/Santiago': 'CL',
  'America/Punta_Arenas': 'CL',
  'Pacific/Easter': 'CL',
  'America/Bogota': 'CO',
  'America/Lima': 'PE',
  'America/Montevideo': 'UY',
  'America/Asuncion': 'PY',
  'America/La_Paz': 'BO',
  'America/Guayaquil': 'EC',
  'Pacific/Galapagos': 'EC',
  'America/Toronto': 'CA',
  'America/Vancouver': 'CA',
  'America/Edmonton': 'CA',
  'America/Winnipeg': 'CA',
  'America/Halifax': 'CA',
  'America/St_Johns': 'CA',
  'America/New_York': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Los_Angeles': 'US',
  'America/Phoenix': 'US',
  'America/Anchorage': 'US',
  'Pacific/Honolulu': 'US',
}

// Language/locale → country fallback
const LANG_TO_COUNTRY: Record<string, string> = {
  'es-MX': 'MX',
  'es-CL': 'CL',
  'es-CO': 'CO',
  'es-PE': 'PE',
  'es-UY': 'UY',
  'es-PY': 'PY',
  'es-BO': 'BO',
  'es-EC': 'EC',
  'en-US': 'US',
  'en-CA': 'CA',
  'fr-CA': 'CA',
}

function detectByTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    return TIMEZONE_TO_COUNTRY[tz] || ''
  } catch {
    return ''
  }
}

function detectByLanguage(): string {
  try {
    const langs = navigator.languages || [navigator.language]
    for (const lang of langs) {
      const match = LANG_TO_COUNTRY[lang]
      if (match) return match
    }
    for (const lang of langs) {
      const parts = lang.split('-')
      if (parts[1]) {
        const region = parts[1].toUpperCase()
        if (PRICE_MAP[region]) return region
      }
    }
    return ''
  } catch {
    return ''
  }
}

async function fetchWithTimeout(url: string, ms: number): Promise<Response> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), ms)
  try {
    const res = await fetch(url, { signal: controller.signal })
    return res
  } finally {
    clearTimeout(timer)
  }
}

async function detectByIP(): Promise<string> {
  const apis = [
    {
      url: 'https://ipwho.is/',
      extract: (data: any) => data.country_code,
    },
    {
      url: 'https://ip-api.com/json/?fields=countryCode',
      extract: (data: any) => data.countryCode,
    },
    {
      url: 'https://ipapi.co/json/',
      extract: (data: any) => data.country_code,
    },
  ]

  for (const api of apis) {
    try {
      const response = await fetchWithTimeout(api.url, 3000)
      if (!response.ok) continue
      const data = await response.json()
      const code = api.extract(data)?.toUpperCase()
      if (code && code.length === 2) return code
    } catch {
      continue
    }
  }

  return ''
}

async function detectCountry(): Promise<string> {
  // 1. Try IP-based geolocation first (most reliable)
  const ipCountry = await detectByIP()
  if (ipCountry && PRICE_MAP[ipCountry]) return ipCountry

  // 2. Fallback: timezone detection (instant, no network)
  const tzCountry = detectByTimezone()
  if (tzCountry && PRICE_MAP[tzCountry]) return tzCountry

  // 3. Fallback: language/locale detection (instant, no network)
  const langCountry = detectByLanguage()
  if (langCountry && PRICE_MAP[langCountry]) return langCountry

  return ''
}

export function useGeoPricing(): PriceData {
  const [prices, setPrices] = useState<{ current: string; compare: string }>(
    DEFAULT_PRICES,
  )
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (cachedCountry) {
      setPrices(PRICE_MAP[cachedCountry] || DEFAULT_PRICES)
      setIsLoading(false)
      return
    }

    detectCountry().then((countryCode) => {
      cachedCountry = countryCode
      setPrices(PRICE_MAP[countryCode] || DEFAULT_PRICES)
      setIsLoading(false)
    })
  }, [])

  return {
    currentPrice: prices.current,
    comparePrice: prices.compare,
    isLoading,
  }
}
